import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Plus, X, Download, Loader2, CheckCircle2 } from "lucide-react";
import { adminApi } from "@/lib/admin";

/**
 * Admin section — research invites.
 *
 * Flow:
 *   1. Admin creates an invite (optional email + label).
 *   2. Copy the resulting URL and share it with the invitee out of band.
 *   3. Invitee registers via that URL. Their user doc is auto-stamped
 *      with training_consent = true and research_invited = true.
 *   4. Admin exports either everyone's chats (JSONL) or one invitee's.
 *
 * The JSONL export triggers a straight file download via a hidden anchor —
 * no XHR/blob roundtrip, since the streamed response can be arbitrarily
 * large (100+ MB for a real cohort) and we don't want to pull it into
 * memory before saving.
 */
export default function ResearchInvitesSection() {
  const [invites, setInvites] = useState(null);
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () =>
    adminApi
      .listResearchInvites()
      .then((d) => setInvites(d?.invites || []))
      .catch(() => setInvites([]));

  useEffect(() => {
    load();
  }, []);

  const inviteUrl = (token) =>
    `${window.location.origin}/register?invite=${encodeURIComponent(token)}`;

  const onCreate = async (e) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    try {
      const created = await adminApi.createResearchInvite({
        email: email.trim() || undefined,
        label: label.trim() || undefined,
      });
      setEmail("");
      setLabel("");
      setInvites((prev) => [created, ...(prev || [])]);
      // Auto-copy the URL so the admin can paste it wherever immediately.
      try {
        await navigator.clipboard.writeText(inviteUrl(created.token));
        toast.success("Invite created and URL copied");
      } catch {
        toast.success("Invite created");
      }
    } catch (err) {
      toast.error(err.message || "Couldn't create invite.");
    } finally {
      setCreating(false);
    }
  };

  const onRevoke = async (token) => {
    if (!confirm("Revoke this invite? The URL will stop working. Existing signups keep their consent.")) return;
    try {
      await adminApi.revokeResearchInvite(token);
      setInvites((prev) =>
        (prev || []).map((i) => (i.token === token ? { ...i, status: "revoked" } : i))
      );
    } catch (err) {
      toast.error(err.message || "Couldn't revoke.");
    }
  };

  const onCopy = async (token) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      toast.success("Invite URL copied");
    } catch {
      toast.error("Copy failed — long-press to select");
    }
  };

  // Cookie auth carries admin identity, so we just direct-navigate to the
  // export endpoint. The browser will follow the Content-Disposition
  // header and save the file.
  const downloadAll = () => {
    window.location.href = adminApi.researchExportAllUrl();
  };
  const downloadUser = (userId) => {
    window.location.href = adminApi.researchExportUserUrl(userId);
  };

  const activeCount = (invites || []).filter((i) => i.status === "active").length;
  const usedCount = (invites || []).filter((i) => i.status === "used").length;

  return (
    <div className="space-y-6" data-testid="research-invites-section">
      <p className="font-sans text-sm text-[var(--text-muted)] leading-relaxed">
        Invite a test user by URL. When they register through the URL,
        their <span className="text-[var(--accent)]">training consent</span>{" "}
        is set automatically and every conversation they save flows into
        the training corpus. Their chats are also exportable as JSONL —
        use them for research, fine-tuning, or evaluation.
      </p>

      {/* Create form */}
      <form onSubmit={onCreate} className="grid md:grid-cols-3 gap-3 items-end" data-testid="research-invite-create-form">
        <label className="flex flex-col gap-1">
          <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
            Email (optional hint)
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="researcher@lab.example"
            className="bg-[var(--bg-pill)] border border-[var(--border)] px-3 py-2 text-[var(--text)] focus:border-[var(--accent)] outline-none"
            data-testid="research-invite-email"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
            Label (your own note)
          </span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Feb cohort — corpus"
            maxLength={120}
            className="bg-[var(--bg-pill)] border border-[var(--border)] px-3 py-2 text-[var(--text)] focus:border-[var(--accent)] outline-none"
            data-testid="research-invite-label"
          />
        </label>
        <button
          type="submit"
          disabled={creating}
          className="h-[42px] px-4 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 font-mono-ui text-[10px] tracking-[0.24em] uppercase inline-flex items-center justify-center gap-2 transition-colors"
          data-testid="research-invite-create-btn"
        >
          {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          {creating ? "Creating…" : "Create invite"}
        </button>
      </form>

      {/* Bulk export */}
      <div className="flex flex-wrap items-center gap-3 border-t border-[var(--border-soft)] pt-5">
        <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
          {activeCount} active · {usedCount} claimed
        </span>
        <button
          onClick={downloadAll}
          disabled={usedCount === 0}
          className="ml-auto px-4 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 font-mono-ui text-[10px] tracking-[0.22em] uppercase inline-flex items-center gap-2 transition-colors"
          data-testid="research-export-all-btn"
        >
          <Download size={12} />
          Export all research chats (JSONL)
        </button>
      </div>

      {/* Invite list */}
      {invites === null ? (
        <p className="font-sans text-sm text-[var(--text-dim)]">Loading invites…</p>
      ) : invites.length === 0 ? (
        <p className="font-sans text-sm text-[var(--text-dim)]">No invites yet. Create one above.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="research-invites-table">
            <thead>
              <tr className="text-left border-b border-[var(--border)]">
                <th className="py-2 pr-4 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)]">Status</th>
                <th className="py-2 pr-4 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)]">Email hint</th>
                <th className="py-2 pr-4 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)]">Label</th>
                <th className="py-2 pr-4 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)]">Created</th>
                <th className="py-2 pr-4 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => {
                const active = inv.status === "active";
                const used = inv.status === "used";
                return (
                  <tr
                    key={inv.token}
                    data-testid={`invite-row-${inv.token.slice(0, 8)}`}
                    className="border-b border-[var(--border-soft)]"
                  >
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-flex items-center gap-1.5 font-mono-ui text-[10px] tracking-[0.22em] uppercase px-2 py-0.5 border ${
                          active
                            ? "border-[var(--accent)] text-[var(--accent)]"
                            : used
                            ? "border-[var(--success)] text-[var(--success)]"
                            : "border-[var(--text-dim)] text-[var(--text-dim)]"
                        }`}
                      >
                        {used && <CheckCircle2 size={10} />}
                        {inv.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-[var(--text-muted)]">{inv.email || <span className="text-[var(--text-dim)]">—</span>}</td>
                    <td className="py-3 pr-4 text-[var(--text-muted)]">{inv.label || <span className="text-[var(--text-dim)]">—</span>}</td>
                    <td className="py-3 pr-4 text-[var(--text-dim)] font-mono-ui text-[11px]">
                      {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap gap-2">
                        {active && (
                          <button
                            onClick={() => onCopy(inv.token)}
                            className="inline-flex items-center gap-1.5 px-2 py-1 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] font-mono-ui text-[9px] tracking-[0.22em] uppercase transition-colors"
                            data-testid={`invite-copy-${inv.token.slice(0, 8)}`}
                          >
                            <Copy size={10} />
                            Copy URL
                          </button>
                        )}
                        {used && inv.used_by_user_id && (
                          <button
                            onClick={() => downloadUser(inv.used_by_user_id)}
                            className="inline-flex items-center gap-1.5 px-2 py-1 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] font-mono-ui text-[9px] tracking-[0.22em] uppercase transition-colors"
                            data-testid={`invite-export-${inv.token.slice(0, 8)}`}
                          >
                            <Download size={10} />
                            Export chats
                          </button>
                        )}
                        {active && (
                          <button
                            onClick={() => onRevoke(inv.token)}
                            className="inline-flex items-center gap-1.5 px-2 py-1 border border-[var(--border)] hover:border-red-400 hover:text-red-400 font-mono-ui text-[9px] tracking-[0.22em] uppercase transition-colors"
                            data-testid={`invite-revoke-${inv.token.slice(0, 8)}`}
                          >
                            <X size={10} />
                            Revoke
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
